import type { QueryResult } from './colimind-types';
import { isKpiCol, isNumericType, isTimeCol } from './colimind-utils';

export const ALERT_COLS = ['status', 'severity', 'alert_type', 'level'];

/** "colimoAlertId" and "avg_cpi" become "colimo alert id" and "avg cpi". */
export function humanizeColumnName(name: string): string {
  return name.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_+/g, ' ').trim().toLowerCase();
}

export function formatCellValue(column: QueryResult['columns'][number] | undefined, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  const s = String(value);
  const col = (column?.name ?? '').toLowerCase();
  const isDate = (column && (column.type_name === 'TIMESTAMP' || column.type_name === 'DATE'))
    || col.includes('timestamp') || col.endsWith('_at') || col.endsWith('_utc') || col === 'date';
  if (isDate) {
    const d = new Date(s);
    if (!isNaN(d.getTime()))
      return d.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
  }
  const n = parseFloat(s);
  if (!isNaN(n) && s === String(n)) {
    if (isKpiCol(col)) return (n * 100).toFixed(1) + ' %';
    if (col.includes('delta_e') || col === 'de') return n.toFixed(2);
    if (Number.isInteger(n)) return s;
    return n.toFixed(3);
  }
  return s;
}

export function severityChipColor(colName: string, value: unknown): 'warn' | 'accent' | 'primary' | '' {
  if (!ALERT_COLS.includes(colName.toLowerCase())) return '';
  const val = String(value).toUpperCase();
  if (val.includes('CRITICAL') || val.includes('RED') || val.includes('ERROR')) return 'warn';
  if (val.includes('WARN') || val.includes('YELLOW') || val.includes('ORANGE')) return 'accent';
  if (val.includes('OK') || val.includes('GREEN') || val.includes('INFO') || val.includes('GOOD')) return 'primary';
  return '';
}

/** Sort key for a table cell: numbers by value, time columns by date, everything else as text. */
export function cellSortValue(column: QueryResult['columns'][number] | undefined, value: unknown): number | string {
  if (value === null || value === undefined || value === '') return '';
  if (column && isNumericType(column)) {
    const n = parseFloat(String(value));
    return isNaN(n) ? '' : n;
  }
  if (column && isTimeCol(column)) {
    const t = Date.parse(String(value));
    return isNaN(t) ? String(value).toLowerCase() : t;
  }
  return String(value).toLowerCase();
}
